import { useState } from 'react';
import { Link } from 'react-router';

import { PageContainer } from '@/components/landing/sections';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { HELP_ARTICLES, HELP_GROUPS, type HelpArticle } from './articles';
import { type HelpChromeProps, HelpLayout } from './HelpLayout';

/**
 * ヘルプの索引。
 *
 * 絞り込みはサーバーに問い合わせず、レジストリの配列を照合するだけ。
 * 記事は 10 枚に届かない前提なので、これで足りる (一覧の絞り込みと同じ作法)。
 */
export function HelpIndexPage({ signedIn, home }: HelpChromeProps) {
  const [query, setQuery] = useState('');

  const hits = HELP_ARTICLES.filter((article) =>
    matchesQuery(query, article.title, article.summary, article.keywords.join(' ')),
  );

  return (
    <HelpLayout signedIn={signedIn} home={home}>
      <PageContainer className="pt-10 pb-14 sm:pt-16 sm:pb-20">
        <div className="flex flex-col gap-3 pb-7 sm:gap-4 sm:pb-10">
          <span className="font-mono text-[13px] tracking-[0.14em] text-ink-subtle">HELP</span>
          <h1 className="text-[26px] font-bold tracking-tight sm:text-4xl">ヘルプ</h1>
          <p className="max-w-2xl text-[15px] leading-relaxed text-ink-muted sm:text-base">
            つなぎ方、立ち上げの順番、レポートの数字の意味。 初めてなら「INOU
            の考え方」から読んでください。
          </p>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-line py-4 sm:gap-4 sm:py-5">
          <SearchInput
            value={query}
            onChange={setQuery}
            placeholder="キーワードで絞り込む"
            className="w-full sm:w-72"
          />
          {/* 絞り込みが幅いっぱいまで伸びるので、件数は縮ませない (「8 /」で折れる) */}
          <span className="shrink-0 font-mono text-xs whitespace-nowrap text-ink-faint">
            {hits.length} / {HELP_ARTICLES.length}
          </span>
        </div>

        {hits.length === 0 ? (
          <p className="border border-line bg-surface-raised px-6 py-10 text-center text-sm text-ink-muted">
            該当する記事がありません。
          </p>
        ) : (
          <div className="flex flex-col gap-9 sm:gap-12">
            {HELP_GROUPS.map((group) => {
              const items = hits.filter((article) => article.audience === group.audience);
              if (items.length === 0) return null;

              return (
                <section key={group.audience} className="flex flex-col gap-5">
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-[13px] tracking-[0.14em] text-ink-subtle">
                      {group.eyebrow}
                    </span>
                    <h2 className="text-xl font-bold">{group.label}</h2>
                  </div>
                  <div className="grid gap-3 sm:gap-5 lg:grid-cols-2">
                    {items.map((article) => (
                      <ArticleCard key={article.slug} article={article} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </PageContainer>
    </HelpLayout>
  );
}

function ArticleCard({ article }: { article: HelpArticle }) {
  return (
    <Link
      to={`/help/${article.slug}`}
      className="flex flex-col gap-2 border border-line bg-surface-raised px-4 py-4 transition-colors hover:border-line-bold hover:bg-surface-strong sm:gap-3 sm:px-6 sm:py-6"
    >
      <span className="font-mono text-[11px] tracking-wider text-ink-faint">{article.slug}</span>
      <h3 className="text-base font-bold">{article.title}</h3>
      <p className="text-sm leading-relaxed text-ink-muted">{article.summary}</p>
    </Link>
  );
}
